import React, { useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem, SerializedKit, SerializedKitAsset } from '../../../../../redux/api/fymAPI.types';
import { staleKitAssets } from './staleAssets';
import { themeMessages } from './themeMessages';
import { StaleThemeNote } from './StaleThemeNote';
import { Panel, PanelBody, PanelHead } from '../../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

const NO_ASSETS: SerializedKitAsset[] = [];

interface ThemePanelShellProps {
  accountId: number;
  item: CatalogItem;
  kit: SerializedKit | null;
  assets: SerializedKitAsset[];
  hint?: string;
  onShowStale?: () => void;
  isInDialog: boolean;
  /** The button at the right of the title ("Done"). */
  headAction?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The frame of the Theme panel: the title, the hint, the content and the note for assets made in
 * an older theme or in older colours (q2b). The note counts against the SAVED look of the kit,
 * never a draft: a change shows in the note only once it is saved.
 */
const ThemePanelShellComponent: React.FC<ThemePanelShellProps> = ({
  accountId,
  item,
  kit,
  assets,
  hint,
  onShowStale,
  isInDialog,
  headAction,
  children,
}) => {
  const intl = useIntl();
  const titleId = useId();
  const kitThemeId = kit?.look.themeId;
  const kitPalette = kit?.palette;
  const stale = useMemo(
    () => (kitThemeId === undefined ? NO_ASSETS : staleKitAssets(assets, kitThemeId, kitPalette)),
    [assets, kitPalette, kitThemeId]
  );

  const content = (
    <>
      <PanelHead
        titleAs="h2"
        titleId={titleId}
        title={intl.formatMessage(themeMessages.title)}
        subtitle={
          <>
            <span className={styles.wideOnly}>{hint ?? intl.formatMessage(themeMessages.hint)}</span>
            <span className={styles.narrowOnly}>{hint ?? intl.formatMessage(themeMessages.hintNarrow)}</span>
          </>
        }
        right={headAction}
      />
      <PanelBody stack>
        {children}
        {onShowStale !== undefined && kitThemeId !== undefined && stale.length > 0 && (
          <StaleThemeNote
            accountId={accountId}
            catalogItemUuid={item.uuid}
            themeId={kitThemeId}
            stale={stale}
            onShow={onShowStale}
          />
        )}
      </PanelBody>
    </>
  );

  // In a dialog the dialog is the box, so the panel draws no second box around its parts.
  if (isInDialog) {
    return (
      <section className={styles.panelInDialog} aria-labelledby={titleId}>
        {content}
      </section>
    );
  }
  return (
    <Panel as="section" aria-labelledby={titleId}>
      {content}
    </Panel>
  );
};

export const ThemePanelShell = TypedMemo(ThemePanelShellComponent);
